import { format } from 'date-fns'
import mapValues from 'lodash-es/mapValues'

import {
  CHARACTER_WHITESPACE,
  EMPTY_LIST,
  NOTIFICATION_TYPE,
} from '../../constants/common'
import { CURRENCY_SYMBOL_MAP } from '../../constants/currency'
import { DATE_SHORT_FORMAT } from '../../constants/date'
import {
  RENTAL_PORTAL_LISTING_STATUS,
  RENTAL_PORTAL_LISTING_STATUSES,
} from '../../constants/rentalPortal'
import { PROFILE_ROUTE_NAMES } from '../../constants/route'
import { SERVER_NON_FIELD_ERROR_KEY } from '../../constants/validation'
import { getShortAddress } from '../../utils/address'
import { toUkTimezoneDate } from '../../utils/date'
import {
  getListViewTypeWithoutPreview,
  isListViewTypeBoard,
} from '../../utils/listViewType'
import {
  filterPermittedActionsWithDividers,
  parseActionsOnValidDivider,
} from '../../utils/massActions'
import { formatNumberWithSeparators, parseAmount } from '../../utils/number'
import { getRentalPortalMockMediaUrl } from '../../utils/rentalPortalMockMedia'
import { parseServerValidation } from '../../utils/validation'
import { ConfirmationModal } from '../Confirmation'
import { getCounterValue } from '../Counter'
import { toUserMultiValueWithArchive } from '../User'

import {
  LISTING_ACTION_ACTIVATE,
  LISTING_ACTION_ARCHIVE,
  LISTING_ACTION_ARCHIVE_MASS,
  LISTING_ACTION_DEACTIVATE,
  LISTING_ACTION_EDIT,
  LISTING_ACTION_NOT_ALLOWED_TOOLTIP_KEY,
  LISTING_ACTION_PERMISSION_NAME,
  LISTING_ACTION_RESTORE,
  LISTING_ACTION_RESTORE_MASS,
  LISTING_AMOUNT_MAX,
  LISTING_ARCHIVE_PORTAL_NAMES_LIMIT,
  LISTING_CHANGED_BY_SOMEONE_ELSE_ERROR_CODE,
  LISTING_DAYS_TO_LET_EMPTY_VALUE,
  LISTING_EDIT_CONFLICT_NOTIFICATION_ID,
  LISTING_PORTAL_LABEL,
  LISTING_PORTAL_LOGO_PATH,
  LISTING_PUBLICATION_IN_PROGRESS_ERROR_CODE,
  LISTING_PUBLICATION_LIVE_SYNC_STATUSES,
  LISTING_SERVER_ERROR_AMOUNT_LIMIT_PATTERN,
  LISTING_SERVER_ERROR_CODE_PATTERN,
  LISTING_SERVER_ERROR_TEXT_KEY,
  LISTING_SINGLE_ACTION_BY_MASS_ACTION,
  LISTING_STATUS_BADGES,
  LISTING_UNAVAILABLE_ACTIONS,
  LISTINGS_FILTER_PARAM,
  QA_NAME_LISTING_RESYNC_DISCLOSURE,
} from './RentalPortalListing.constants'
import { getListingPublicationErrorTextKey } from './RentalPortalListingPublication'

// TODO: a deactivated listing goes live again through a portal publish, which
// the row does not open yet
function getIsListingActionUnavailable(action) {
  return LISTING_UNAVAILABLE_ACTIONS.includes(action)
}

function toListingActionItem({ action, item, label, permission }) {
  const disabledTooltipKey = getListingActionDisabledTooltipKey({
    action,
    item,
  })

  return {
    disabled: Boolean(disabledTooltipKey),
    label,
    permission,
    tooltipContent: disabledTooltipKey,
    value: action,
  }
}

function getListingConflictAuthorText({ error, t }) {
  const author = error.response.data.meta.listing.updated_by

  return author
    ? t('RentalPortal.ListingChangedByUserConflictKey', {
        name: author.fullname,
      })
    : t('RentalPortal.ListingChangedConflictKey')
}

function getListingLivePortalsValue(portalNames) {
  const namedPortals = portalNames
    .slice(0, LISTING_ARCHIVE_PORTAL_NAMES_LIMIT)
    .join(', ')

  const hiddenCount = portalNames.length - LISTING_ARCHIVE_PORTAL_NAMES_LIMIT

  return hiddenCount > 0
    ? `${namedPortals} ${getCounterValue(hiddenCount, true)}`
    : namedPortals
}

function toListingAmount(value) {
  return value == null ? null : Number(value)
}

function toListingUser(user) {
  return user
    ? {
        avatar_color: user.avatar_color,
        avatar_thumb_url: user.avatar,
        full_name: user.fullname,
        id: user.id,
        is_archived: user.archived,
      }
    : null
}

function toListingPhoto(photo) {
  return {
    file_id: photo.file.id,
    filename: photo.file.name,
    height: photo.file.height,
    id: photo.id,
    is_cover: photo.is_cover,
    mime_type: photo.file.mime_type,
    position: photo.position,
    size: photo.file.size,
    thumb_url: photo.file.thumbnail ?? photo.file.url,
    uploaded_at: photo.created,
    url: photo.file.url,
    width: photo.file.width,
  }
}

function toListingCoverPhoto(photos) {
  const cover = photos.find(photo => photo.is_cover) ?? photos[0]

  return cover ? { id: cover.id, thumbnail: cover.thumb_url } : null
}

function getIsListingDraft(status) {
  return status === RENTAL_PORTAL_LISTING_STATUS.draft
}

function getIsListingDeactivateAllowed(status) {
  return (
    status === RENTAL_PORTAL_LISTING_STATUS.published ||
    getIsListingDraft(status)
  )
}

function getIsListingActivateAllowed(status) {
  return status === RENTAL_PORTAL_LISTING_STATUS.deactivated
}

function getListingServerErrorText({ field, message, t }) {
  if (
    field === SERVER_NON_FIELD_ERROR_KEY ||
    !LISTING_SERVER_ERROR_CODE_PATTERN.test(message)
  ) {
    return message
  }

  if (LISTING_SERVER_ERROR_TEXT_KEY[message]) {
    return t(LISTING_SERVER_ERROR_TEXT_KEY[message])
  }

  return LISTING_SERVER_ERROR_AMOUNT_LIMIT_PATTERN.test(message)
    ? getListingAmountMaxErrorText(t)
    : t('Error.IncorrectValueKey')
}

function toListingProfileData(listing) {
  const photos = (listing.photos ?? EMPTY_LIST).map(toListingPhoto)

  return {
    ...listing,
    cover_photo: toListingCoverPhoto(photos),
    created_by: toListingUser(listing.created_by),
    deposit_amount: toListingAmount(listing.deposit_amount),
    display_name: listing.listing_title,
    heating_costs: toListingAmount(listing.heating_costs),
    other_costs: toListingAmount(listing.other_costs),
    // TODO: the detail serves no tab permission besides can_view_documents, so
    // the applicants, publications, updates and viewings tabs open on can_view
    permissions: {
      ...listing.permissions,
      can_edit_content: listing.permissions.can_edit,
      can_edit_responsible_user: listing.permissions.can_edit,
      can_view_applicants: listing.permissions.can_view,
      can_view_publications: listing.permissions.can_view,
      can_view_updates: listing.permissions.can_view,
      can_view_viewings: listing.permissions.can_view,
    },
    photos,
    portal_publications: listing.publications,
    rent_price: toListingAmount(listing.rent_price),
    responsible_user: toListingUser(listing.responsible_manager),
    service_charge: toListingAmount(listing.service_charge),
    updated_by: toListingUser(listing.updated_by),
    warmmiete_derived: toListingAmount(listing.warmmiete_derived),
  }
}

export function getIsListingActionRestoreMass(action) {
  return action === LISTING_ACTION_RESTORE_MASS
}

export function getIsListingActionRestore(action) {
  return action === LISTING_ACTION_RESTORE
}

export function getListingAmountDisplayValue({ amount, currency }) {
  return amount != null
    ? `${formatNumberWithSeparators(amount)} ${CURRENCY_SYMBOL_MAP[currency] ?? currency}`
    : null
}

export function getListingRentDisplayValue({
  currency,
  rent_price: rentPrice,
}) {
  return getListingAmountDisplayValue({ amount: rentPrice, currency })
}

export function getListingDraftFormValues(draft) {
  return draft?.values
}

export function getListingDraftSavedAt(draft) {
  return draft?.savedAt
}

export function getIsListingPublished(status) {
  return status === RENTAL_PORTAL_LISTING_STATUS.published
}

export function getIsListingClosed(status) {
  return status === RENTAL_PORTAL_LISTING_STATUS.closed
}

export function getIsListingDeactivated(status) {
  return status === RENTAL_PORTAL_LISTING_STATUS.deactivated
}

export function getListingActionDisabledTooltipKey({ action, item }) {
  const { permissions, status } = item

  if (getIsListingActionUnavailable(action)) {
    return 'RentalPortal.ListingActionUnavailableTooltipKey'
  }

  if (!permissions[LISTING_ACTION_PERMISSION_NAME[action]]) {
    return LISTING_ACTION_NOT_ALLOWED_TOOLTIP_KEY[action]
  }

  return action === LISTING_ACTION_DEACTIVATE && getIsListingDraft(status)
    ? 'RentalPortal.ListingDeactivateOnlyPublishedTooltipKey'
    : null
}

export function getListingTableItemActions(item) {
  const { status } = item

  const items = [
    toListingActionItem({
      action: LISTING_ACTION_EDIT,
      item,
      label: 'RentalPortal.ListingEditActionOptionKey',
      permission: true,
    }),
    toListingActionItem({
      action: LISTING_ACTION_DEACTIVATE,
      item,
      label: 'RentalPortal.ListingDeactivateActionOptionKey',
      permission: getIsListingDeactivateAllowed(status),
    }),
    toListingActionItem({
      action: LISTING_ACTION_ACTIVATE,
      item,
      label: 'RentalPortal.ListingActivateActionOptionKey',
      permission: getIsListingActivateAllowed(status),
    }),
    null,
    toListingActionItem({
      action: LISTING_ACTION_ARCHIVE,
      item,
      label: 'RentalPortal.ListingArchiveActionOptionKey',
      permission: true,
    }),
  ]

  return parseActionsOnValidDivider(filterPermittedActionsWithDividers(items))
}

export function getIsListingArchivable(listing) {
  return Boolean(listing?.permissions?.can_archive)
}

export function getIsListingReadOnly(listing) {
  return Boolean(listing) && !listing.permissions?.can_edit
}

export function getListingsMassActionNotificationTitle({
  action,
  count,
  t,
  totalCount,
}) {
  const titleAction =
    totalCount > 1
      ? action
      : (LISTING_SINGLE_ACTION_BY_MASS_ACTION[action] ?? action)

  const titles = {
    [LISTING_ACTION_ARCHIVE]: t('Notification.CommonTitleArchivedKey'),
    [LISTING_ACTION_ARCHIVE_MASS]: t(
      'Notification.CommonTitleMassArchivedKey',
      { count, totalCount }
    ),
    [LISTING_ACTION_RESTORE]: t('Notification.CommonTitleRestoredKey'),
    [LISTING_ACTION_RESTORE_MASS]: t(
      'Notification.CommonTitleMassRestoredKey',
      { count, totalCount }
    ),
  }

  return titles[titleAction] || null
}

export function getListingsMassActions({ eligibleCount, selectedSize, t }) {
  const hasUnusableSelection = selectedSize > 0 && eligibleCount === 0

  const isUnavailable = getIsListingActionUnavailable(
    LISTING_ACTION_ARCHIVE_MASS
  )

  const items = [
    {
      disabled: isUnavailable || hasUnusableSelection,
      label: t('RentalPortal.ListingArchiveActionOptionKey'),
      permission: true,
      tooltipContent: isUnavailable
        ? t('RentalPortal.ListingActionUnavailableTooltipKey')
        : hasUnusableSelection
          ? t('RentalPortal.ListingArchiveMassNotAllowedTooltipKey')
          : null,
      value: LISTING_ACTION_ARCHIVE_MASS,
    },
  ]

  return parseActionsOnValidDivider(filterPermittedActionsWithDividers(items))
}

export function getListingArchiveDescription({ livePortalNames, name, t }) {
  return [
    t('RentalPortal.ListingArchiveConfirmationTextKey', { name }),
    livePortalNames.length > 0
      ? t('RentalPortal.ListingArchiveLivePortalsWarningKey', {
          portals: getListingLivePortalsValue(livePortalNames),
        })
      : null,
  ]
    .filter(Boolean)
    .join(CHARACTER_WHITESPACE)
}

export function getListingsFilteredEmptyTextKey({
  filterValues,
  listViewType,
}) {
  if (filterValues?.[LISTINGS_FILTER_PARAM.needsMyAction]) {
    return 'RentalPortal.ListingNeedsMyActionNoResultsKey'
  }

  return isListViewTypeBoard(listViewType)
    ? 'RentalPortal.ListingBoardNoFilterResultsKey'
    : 'RentalPortal.ListingNoFilterResultsKey'
}

export function getListingsRefreshErrorText({
  availableFilters,
  filterValues,
  t,
}) {
  const activeFilterTitles = availableFilters
    .filter(filter =>
      [filter.query_param].flat().some(param => filterValues?.[param])
    )
    .map(filter => t(filter.title))

  return activeFilterTitles.length > 0
    ? t('RentalPortal.ListingRefreshErrorFiltersKey', {
        filters: activeFilterTitles.join(', '),
      })
    : t('RentalPortal.ListingRefreshErrorKey')
}

export function toListingUserCellValue(user) {
  return user ? toUserMultiValueWithArchive(user) : null
}

export function getListingProfileLink(id) {
  return PROFILE_ROUTE_NAMES.rentalPortalListingProfile({ id })
}

export function getListingsEffectiveViewType({ hasPreview, listViewType }) {
  return hasPreview ? listViewType : getListViewTypeWithoutPreview(listViewType)
}

export function getListingMissingPublishItems({ listing, t }) {
  return (listing?.publish_readiness ?? EMPTY_LIST)
    .filter(check => !check.is_done)
    .map(check => t(check.label))
}

export function getListingPublishBlockReason({ missingItems, t }) {
  return missingItems.length > 0
    ? t('RentalPortal.ListingFormPublishBlockedKey', {
        missing: missingItems.join(', '),
      })
    : undefined
}

export function getListingAddressPublicTooltipKey(isPublic) {
  return isPublic
    ? 'RentalPortal.ListingAddressPublicShownTooltipKey'
    : 'RentalPortal.ListingAddressPublicHiddenTooltipKey'
}

export function getListingPortalLabel(portal) {
  return LISTING_PORTAL_LABEL[portal] ?? portal
}

export function getListingPublicationErrorText({ publication, t }) {
  return (
    publication.last_error?.message ||
    t(getListingPublicationErrorTextKey(publication), {
      portal: getListingPortalLabel(publication.portal),
    })
  )
}

export function getListingPortalLogoUrl(portal) {
  return getRentalPortalMockMediaUrl(LISTING_PORTAL_LOGO_PATH[portal])
}

export function getHasListingPublicationPublicUrl(publication) {
  return Boolean(publication?.public_url)
}

export function getIsListingPublicationLive(publication) {
  return LISTING_PUBLICATION_LIVE_SYNC_STATUSES.includes(
    publication.sync_status
  )
}

export function getHasListingLivePublications(listing) {
  return (listing?.portal_publications ?? EMPTY_LIST).some(
    getIsListingPublicationLive
  )
}

export function getHasListingBeenDispatched(listing) {
  return Boolean(
    listing?.has_been_published || listing?.portal_publications?.length > 0
  )
}

export function getListingResyncDisclosureModal({
  listingName,
  t,
  onDismiss,
  onSubmit,
}) {
  return (
    <ConfirmationModal
      actionButtonsProps={{ submitText: t('Modal.SaveBtnKey') }}
      description={t('RentalPortal.ListingResyncDisclosureTextKey', {
        name: listingName,
      })}
      qaName={QA_NAME_LISTING_RESYNC_DISCLOSURE}
      title={t('RentalPortal.ListingResyncDisclosureTitleKey')}
      onCancel={onDismiss}
      onClose={onDismiss}
      onSubmit={onSubmit}
    />
  )
}

export function getListingBuildingShortAddress(building) {
  return getShortAddress(building?.address?.value)
}

export function toListingFlatNumber(value) {
  return value == null ? null : Number(value)
}

export function getListingUnitLabel({ listing, t }) {
  const flat = listing?.flat

  return flat
    ? t('RentalPortal.ListingUnitLabelKey', {
        address: getListingBuildingShortAddress(flat.building),
        number: flat.number,
      })
    : null
}

export function getListingConversionDisplayValue(value) {
  return Number.isFinite(value) ? `${value} %` : null
}

export function getListingReportingDisplayValue(value) {
  return Number.isFinite(value) ? String(value) : null
}

export function getListingDaysToLetDisplayValue(value) {
  return Number.isFinite(value)
    ? String(value)
    : LISTING_DAYS_TO_LET_EMPTY_VALUE
}

export function getListingStatusBadge({ listing, t }) {
  const badge = LISTING_STATUS_BADGES[listing?.status_badge?.key]

  return badge
    ? {
        isWarning: Boolean(badge.isWarning),
        label: t(badge.text, { count: listing.status_badge.error_count }),
        textColor: badge.textColor,
      }
    : null
}

export function getIsListingPublishBlocked(listing) {
  return Boolean(listing?.flat_published_listing)
}

export function getListingAlreadyPublishedTooltip({
  flatPublishedListing,
  t,
  timezone,
}) {
  return t('RentalPortal.ListingAlreadyPublishedTooltipKey', {
    date: flatPublishedListing?.first_published_at
      ? format(
          toUkTimezoneDate(flatPublishedListing.first_published_at, timezone),
          DATE_SHORT_FORMAT
        )
      : null,
    manager: flatPublishedListing?.responsible_manager?.fullname,
    title: getListingFlatPublishedListingTitle({ flatPublishedListing, t }),
  })
}

export function getListingFlatPublishedListingTitle({
  flatPublishedListing,
  t,
}) {
  return (
    flatPublishedListing?.listing_title ??
    t('RentalPortal.ListingProfileHeaderNoTitleKey')
  )
}

export function getListingCoverThumbnail(listing) {
  return listing?.cover_photo?.thumbnail ?? null
}

export function getIsListingPublicationInProgressError(error) {
  return (
    error?.response?.data?.errors === LISTING_PUBLICATION_IN_PROGRESS_ERROR_CODE
  )
}

export function getIsListingChangedConflictError(error) {
  return (
    error?.response?.data?.errors === LISTING_CHANGED_BY_SOMEONE_ELSE_ERROR_CODE
  )
}

export function getListingConflictVersion(error) {
  return error.response.data.meta.listing.version
}

export function getListingConflictNotification({ error, t }) {
  return {
    text: t('RentalPortal.ListingChangeNotSavedConflictKey'),
    title: getListingConflictAuthorText({ error, t }),
  }
}

export function getListingDeactivateConflictNotification({ error, t }) {
  return {
    text: t('RentalPortal.ListingDeactivateConflictKey', {
      status: t(
        RENTAL_PORTAL_LISTING_STATUSES[error.response.data.meta.listing.status]
          .text
      ),
    }),
    title: getListingConflictAuthorText({ error, t }),
  }
}

export function getListingEditConflictNotification({
  error,
  t,
  onClickOverwrite,
  onClickReload,
}) {
  return {
    actions: [
      {
        callback: onClickReload,
        text: t('RentalPortal.ListingEditConflictReloadBtnKey'),
      },
      {
        callback: onClickOverwrite,
        text: t('RentalPortal.ListingEditConflictOverwriteBtnKey'),
      },
    ],
    id: LISTING_EDIT_CONFLICT_NOTIFICATION_ID,
    text: [
      getListingConflictAuthorText({ error, t }),
      t('RentalPortal.ListingEditConflictChoiceKey'),
    ].join(CHARACTER_WHITESPACE),
    type: NOTIFICATION_TYPE.warning,
  }
}

export function getListingProfilePreparedData(listing) {
  return listing ? toListingProfileData(listing) : null
}

export function getListingTransition({ key, listing }) {
  return (listing?.available_transitions ?? EMPTY_LIST).find(
    transition => transition.key === key
  )
}

export function getIsListingAmountWithinMax(value) {
  return !value || parseAmount(value) <= LISTING_AMOUNT_MAX
}

export function getListingAmountMaxErrorText(t) {
  return t('Error.MaxValueKey', {
    value: formatNumberWithSeparators(LISTING_AMOUNT_MAX),
  })
}

export function getListingServerFieldErrors({ error, t }) {
  return mapValues(
    parseServerValidation(error?.response?.data),
    (message, field) => getListingServerErrorText({ field, message, t })
  )
}
